import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, toastError } from "../../lib/api";
import { useEffectiveRole, canAtLeast } from "../../lib/roles";
import { useAuth } from "../../context/AuthContext";
import { hasActionAccess } from "../../lib/workspaces";
import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Archive, ClipboardCheck, Download, FileText, Plus, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["referred", "contacted", "qualified", "converted", "closed_lost"] as const;
const TEST_RESULTS = ["negative", "mild", "moderate", "severe", "very_severe"] as const;
type ReferralStatus = typeof STATUSES[number];
type TestResult = typeof TEST_RESULTS[number];
const resultLabel = (value: string) => value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

export const canAccessPatientCompliance = (role: string) =>
  role === "owner_admin" || role === "admin_support";

export const stagePendingTestResult = (
  current: Record<string, TestResult>,
  referralId: string,
  severity: TestResult,
  saved?: TestResult,
) => {
  const next = { ...current };
  if (severity === saved) delete next[referralId];
  else next[referralId] = severity;
  return next;
};

export const stagePendingReferralStatus = (
  current: Record<string, ReferralStatus>,
  referralId: string,
  status: ReferralStatus,
  saved: ReferralStatus,
) => {
  const next = { ...current };
  if (status === saved) delete next[referralId];
  else next[referralId] = status;
  return next;
};

type Referral = {
  id: string;
  referrer_contact_id: string;
  patient_contact_id: string;
  referrer_name: string;
  patient_name: string;
  patient_lifecycle_status: string | null;
  assigned_user_name: string | null;
  organization_id: string;
  organization_name: string | null;
  status: ReferralStatus;
  notes: string | null;
  remarks: string | null;
  updated_at: string | null;
  archived?: boolean;
};
type OsaResult = {
  id: string;
  referral_id: string;
  severity: TestResult;
  tested_at: string;
  cpap_purchased: boolean;
  cpap_purchase_date?: string | null;
  commission_eligibility: string;
};

type ContactChoice = {
  id: string;
  first_name: string;
  last_name: string;
  lifecycle_status: string;
  archived?: boolean;
};
type DuplicateCandidate = {
  id: string; first_name: string; last_name?: string; email?: string | null;
  phone?: string | null; score: number; reasons: string[]; lifecycle_status?: string | null;
};
type OrganizationAffiliation = {
  id: string; organization_id: string; organization_name: string | null;
  active: boolean; is_primary: boolean; organization_archived?: boolean;
};
type QueueResponse = { items: Referral[]; page: number; limit: number; total: number };
type ComplianceResponse = { report: ComplianceReport | null; history: ComplianceReport[]; can_edit: boolean };
type PatientScheduleNote = {
  id: string; note: string; created_at: string; created_by_name: string;
};
type PatientScheduleHistory = {
  id: string; start_at: string; end_at?: string | null; purpose: string; status: string;
  assigned_to_name: string; notes: PatientScheduleNote[]; created_at: string; updated_at: string;
};
type ComplianceReport = {
  id: string; report_date: string; period_start?: string | null; period_end?: string | null;
  ahi?: number | null; leak?: number | null; leak_unit?: string | null;
  pressure?: number | null; pressure_unit?: string | null; pressure_mode?: string | null;
  usage_hours?: number | null; usage_days_used?: number | null; usage_days_total?: number | null;
  usage_percent?: number | null; notes?: string | null; source_filename: string;
  clinician_name?: string | null; encoded_by_role?: string | null;
  uploaded_by_name?: string | null; created_at: string;
};
const blankCompliance = {
  report_date: "", clinician_name: "", period_start: "", period_end: "", ahi: "", leak: "", leak_unit: "L/min",
  pressure: "", pressure_unit: "cmH2O", pressure_mode: "", usage_hours: "",
  usage_days_used: "", usage_days_total: "", usage_percent: "", notes: "",
};

const blankForm = {
  referrer_contact_id: "", organization_id: "", patient_contact_id: "", first_name: "", last_name: "",
  email: "", phone: "", location: "", status: "referred" as ReferralStatus, notes: "", remarks: "",
};

export default function Referrals() {
  const role = useEffectiveRole();
  const { user } = useAuth();
  const canOperate = canAtLeast(role, "representative") && hasActionAccess(user, "referrals", "view");
  const canAdd = canAtLeast(role, "manager_trainer") && hasActionAccess(user, "referrals", "add");
  const canEdit = canAtLeast(role, "manager_trainer") && hasActionAccess(user, "referrals", "edit");
  const canArchive = canAtLeast(role, "manager_trainer") && hasActionAccess(user, "referrals", "archive");
  const canRestore = canAtLeast(role, "manager_trainer") && hasActionAccess(user, "referrals", "restore");
  const canAccessCompliance = canAccessPatientCompliance(role);
  const canEditCompliance = canAccessCompliance && canEdit;
  const canViewOsa = hasActionAccess(user, "osa_results", "view");
  const canAddOsa = canAtLeast(role, "manager_trainer") && hasActionAccess(user, "osa_results", "add");
  const canEditOsa = canAtLeast(role, "manager_trainer") && hasActionAccess(user, "osa_results", "edit");
  const [rows, setRows] = useState<Referral[]>([]);
  const [osaRows, setOsaRows] = useState<OsaResult[]>([]);
  const [pendingTestResults, setPendingTestResults] = useState<Record<string, TestResult>>({});
  const [pendingStatuses, setPendingStatuses] = useState<Record<string, ReferralStatus>>({});
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ContactChoice[]>([]);
  const [affiliations, setAffiliations] = useState<OrganizationAffiliation[]>([]);
  const [status, setStatus] = useState("all");
  const [testResult, setTestResult] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [patientMode, setPatientMode] = useState<"new" | "existing">("new");
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [compliance, setCompliance] = useState<ComplianceReport | null>(null);
  const [reportHistory, setReportHistory] = useState<ComplianceReport[]>([]);
  const [appointmentHistory, setAppointmentHistory] = useState<PatientScheduleHistory[]>([]);
  const [editingCompliance, setEditingCompliance] = useState(false);
  const [complianceForm, setComplianceForm] = useState(blankCompliance);
  const [complianceFile, setComplianceFile] = useState<File | null>(null);
  const [complianceSaving, setComplianceSaving] = useState(false);
  const [duplicateCandidates, setDuplicateCandidates] = useState<DuplicateCandidate[]>([]);
  const [duplicateCandidateId, setDuplicateCandidateId] = useState("");
  const [duplicateOverrideReason, setDuplicateOverrideReason] = useState("");
  const limit = 50;

  const load = useCallback(async () => {
    if (!canOperate) return;
    try {
      const { data } = await api.get<QueueResponse>("/crm/referrals", {
        params: { status: status === "all" ? "" : status, search: appliedSearch, page, limit, archived: showArchived },
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
      if (canViewOsa) {
        const resultResponse = await api.get<OsaResult[]>("/clinical/osa-results");
        setOsaRows(resultResponse.data || []);
      } else {
        setOsaRows([]);
      }
    } catch (error) { toastError(error); }
  }, [canOperate, canViewOsa, status, appliedSearch, page, showArchived]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canOperate) return;
    api.get<ContactChoice[]>("/crm/contacts", { params: { archived: false, include_patients: true, limit: 500 } })
      .then(({ data }) => setContacts((data || []).filter((row) => !row.archived)))
      .catch(toastError);
  }, [canOperate]);

  const patientIds = useMemo(() => new Set(rows.map((row) => row.patient_contact_id)), [rows]);
  const referrerContacts = useMemo(() => contacts.filter((row) => !patientIds.has(row.id)), [contacts, patientIds]);
  const availablePatients = useMemo(
    () => contacts.filter((row) => patientIds.has(row.id) && row.id !== form.referrer_contact_id),
    [contacts, form.referrer_contact_id, patientIds],
  );
  const resultByReferral = useMemo(
    () => new Map(osaRows.map((result) => [result.referral_id, result])),
    [osaRows],
  );
  const visibleRows = useMemo(
    () => testResult === "all"
      ? rows
      : rows.filter((row) => {
          const result = resultByReferral.get(row.id);
          return testResult === "pending" ? !result : result?.severity === testResult;
        }),
    [rows, resultByReferral, testResult],
  );

  const selectReferrer = async (contactId: string) => {
    setForm({ ...form, referrer_contact_id: contactId, organization_id: "", patient_contact_id: "" });
    setAffiliations([]);
    if (!contactId) return;
    try {
      const { data } = await api.get(`/crm/contacts/${contactId}`);
      setAffiliations((data.linked_organizations || []).filter((item: OrganizationAffiliation) => item.active && !item.organization_archived));
    } catch (error) { toastError(error); }
  };

  const createReferral = async (override?: { candidateId: string; reason: string }) => {
    if (!form.referrer_contact_id) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        referrer_contact_id: form.referrer_contact_id,
        organization_id: form.organization_id || null,
        status: form.status,
        notes: form.notes.trim() || null,
        remarks: form.remarks.trim() || null,
      };
      if (patientMode === "existing") {
        if (!form.patient_contact_id) return;
        payload.patient_contact_id = form.patient_contact_id;
      } else {
        if (!form.first_name.trim()) return;
        payload.new_patient = {
          first_name: form.first_name.trim(), last_name: form.last_name.trim(),
          email: form.email.trim() || null, phone: form.phone.trim() || null,
          location: form.location.trim() || null, lifecycle_status: "prospect",
          ...(override ? {
            duplicate_candidate_id: override.candidateId,
            duplicate_override_reason: override.reason,
            duplicate_check_completed: true,
          } : {}),
        };
      }
      await api.post(`/crm/contacts/${form.referrer_contact_id}/referrals`, payload);
      toast.success("Referral added");
      setOpen(false);
      setForm(blankForm);
      setPatientMode("new");
      setDuplicateCandidates([]); setDuplicateCandidateId(""); setDuplicateOverrideReason("");
      setPage(1);
      await load();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "possible_patient_duplicates") {
        setDuplicateCandidates(detail.candidates || []);
        setDuplicateCandidateId(detail.candidates?.[0]?.id || "");
      } else {
        toastError(error);
      }
    } finally { setSaving(false); }
  };

  const linkDuplicatePatient = (candidateId: string) => {
    setPatientMode("existing");
    setForm({ ...form, patient_contact_id: candidateId });
    setDuplicateCandidates([]); setDuplicateCandidateId(""); setDuplicateOverrideReason("");
    toast.success("Existing patient selected. Review and add the referral.");
  };

  const stageReferralStatus = (row: Referral, nextStatus: ReferralStatus) => {
    setPendingStatuses((current) => stagePendingReferralStatus(current, row.id, nextStatus, row.status));
  };

  const stageTestResult = (row: Referral, severity: TestResult) => {
    const saved = resultByReferral.get(row.id)?.severity;
    setPendingTestResults((current) => stagePendingTestResult(current, row.id, severity, saved));
  };

  const cancelRowChanges = (referralId: string) => {
    setPendingStatuses((current) => {
      const next = { ...current };
      delete next[referralId];
      return next;
    });
    setPendingTestResults((current) => {
      const next = { ...current };
      delete next[referralId];
      return next;
    });
  };

  const saveRowChanges = async (row: Referral) => {
    const nextStatus = pendingStatuses[row.id];
    const severity = pendingTestResults[row.id];
    if (!nextStatus && !severity) return;

    const currentResult = resultByReferral.get(row.id);
    const changes = [
      nextStatus ? `Referral Status: ${resultLabel(row.status)} → ${resultLabel(nextStatus)}` : null,
      severity
        ? `Test Result: ${currentResult ? resultLabel(currentResult.severity) : "Not Tested / Pending"} → ${resultLabel(severity)}`
        : null,
    ].filter(Boolean).join("\n");

    if (!window.confirm(
      `Save changes for ${row.patient_name}?\n\n${changes}${severity ? "\n\nThe test result may affect commission eligibility." : ""}`,
    )) return;

    setSavingRowId(row.id);
    try {
      if (nextStatus) {
        await api.patch(`/crm/contacts/${row.referrer_contact_id}/referrals/${row.id}`, { status: nextStatus });
      }
      if (severity) {
        if (currentResult) {
          await api.patch(`/clinical/osa-results/${currentResult.id}`, { severity });
        } else {
          await api.post("/clinical/osa-results", {
            referral_id: row.id,
            severity,
            tested_at: new Date().toISOString().slice(0, 10),
            cpap_purchased: false,
          });
        }
      }
      cancelRowChanges(row.id);
      toast.success("Referral changes saved ✓");
      await load();
    } catch (error) { toastError(error); }
    finally { setSavingRowId(null); }
  };

  const confirmCpapPurchase = async (row: Referral) => {
    const current = resultByReferral.get(row.id);
    if (!current || current.severity === "negative" || current.cpap_purchased) return;
    const purchaseDate = window.prompt("CPAP purchase date (YYYY-MM-DD)", new Date().toISOString().slice(0, 10));
    if (!purchaseDate) return;
    if (!window.confirm(
      `Confirm CPAP purchase for ${row.patient_name} on ${purchaseDate}? This may create a commission for Owner review and cannot be casually reversed.`,
    )) return;
    try {
      await api.patch(`/clinical/osa-results/${current.id}/purchase`, null, { params: { purchase_date: purchaseDate } });
      toast.success("CPAP purchase confirmed and commission eligibility evaluated");
      await load();
    } catch (error) { toastError(error); }
  };

  const lifecycleReferral = async (row: Referral) => {
    const action = row.archived ? "restore" : "archive";
    if (!window.confirm(`${row.archived ? "Restore" : "Archive"} the referral for ${row.patient_name}?`)) return;
    try {
      await api.post(`/crm/contacts/${row.referrer_contact_id}/referrals/${row.id}/${action}`);
      toast.success(row.archived ? "Referral restored" : "Referral archived");
      await load();
    } catch (error) { toastError(error); }
  };


  const openCompliance = async (row: Referral) => {
    setSelectedReferral(row);
    setComplianceOpen(true);
    setCompliance(null);
    setReportHistory([]);
    setAppointmentHistory([]);
    setEditingCompliance(false);
    setComplianceForm(blankCompliance);
    setComplianceFile(null);
    try {
      const [{ data }, { data: scheduleData }] = await Promise.all([
        api.get<ComplianceResponse>(`/crm/referrals/${row.id}/compliance`),
        api.get<{ items: PatientScheduleHistory[] }>(`/patient-calendar/referrals/${row.id}/history`),
      ]);
      const report = data.report;
      setCompliance(report);
      setReportHistory(data.history || []);
      setAppointmentHistory(scheduleData.items || []);
      if (report) setComplianceForm({
        report_date: report.report_date || "", period_start: report.period_start || "", period_end: report.period_end || "",
        ahi: report.ahi?.toString() || "", leak: report.leak?.toString() || "", leak_unit: report.leak_unit || "L/min",
        pressure: report.pressure?.toString() || "", pressure_unit: report.pressure_unit || "cmH2O",
        pressure_mode: report.pressure_mode || "", usage_hours: report.usage_hours?.toString() || "",
        usage_days_used: report.usage_days_used?.toString() || "", usage_days_total: report.usage_days_total?.toString() || "",
        usage_percent: report.usage_percent?.toString() || "", notes: report.notes || "", clinician_name: report.clinician_name || "",
      });
    } catch (error) { toastError(error); }
  };

  const saveCompliance = async () => {
    if (!selectedReferral || !complianceFile || !complianceForm.report_date) return;
    const body = new FormData();
    Object.entries(complianceForm).forEach(([key, value]) => body.append(key, value));
    body.append("file", complianceFile);
    setComplianceSaving(true);
    try {
      const { data } = await api.post(`/crm/referrals/${selectedReferral.id}/compliance`, body);
      setCompliance(data);
      setComplianceFile(null);
      setEditingCompliance(false);
      const refreshed = await api.get<ComplianceResponse>(`/crm/referrals/${selectedReferral.id}/compliance`);
      setCompliance(refreshed.data.report);
      setReportHistory(refreshed.data.history || []);
      toast.success("Latest compliance report updated");
    } catch (error) { toastError(error); } finally { setComplianceSaving(false); }
  };

  const downloadCompliance = async (kind: "source" | "doctor-report") => {
    if (!selectedReferral) return;
    try {
      const response = await api.get(`/crm/referrals/${selectedReferral.id}/compliance/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = kind === "source" ? (compliance?.source_filename || "compliance-report.pdf") : "patient-compliance-summary.pdf";
      document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
    } catch (error) { toastError(error); }
  };

  if (!canOperate) {
    return <div className="p-8 text-sm text-slate-500">You do not have access to the referral operations queue.</div>;
  }

  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <>
      <PageHeader
        title="Referrals"
        subtitle="Add referred patients and manage their progress in one scalable queue."
        actions={canAdd ? <Button onClick={() => setOpen(true)} data-testid="add-referral"><Plus className="w-4 h-4 mr-1" /> Add referral</Button> : undefined}
      />
      <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-4">
        <div className="flex flex-wrap gap-3 rounded-lg border border-slate-200 bg-white p-4">
          <div className="min-w-52 flex-1">
            <Label htmlFor="referral-search">Search patient or referrer</Label>
            <div className="mt-1 flex gap-2">
              <Input id="referral-search" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { setPage(1); setAppliedSearch(search); } }} />
              <Button variant="outline" onClick={() => { setPage(1); setAppliedSearch(search); }} aria-label="Search referrals"><Search className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="w-52">
            <Label>Status</Label>
            <Select value={status} onValueChange={(value) => { setPage(1); setStatus(value); }}>
              <SelectTrigger className="mt-1" data-testid="referral-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((value) => <SelectItem key={value} value={value}>{value.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="w-52">
            <Label>Test Result</Label>
            <Select value={testResult} onValueChange={(value) => setTestResult(value)}>
              <SelectTrigger className="mt-1" data-testid="test-result-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All test results</SelectItem><SelectItem value="pending">Not tested / pending</SelectItem>{TEST_RESULTS.map((value) => <SelectItem key={value} value={value}>{resultLabel(value)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-slate-600"><input type="checkbox" checked={showArchived} onChange={(event) => { setPage(1); setShowArchived(event.target.checked); }} /> Show archived</label>
        </div>
        {!canAdd && !canEdit && !canArchive && !canRestore && !canAddOsa && !canEditOsa && <div className="rounded-md bg-slate-50 px-4 py-3 text-sm text-slate-600">View only — referral changes are disabled for your account.</div>}

        {visibleRows.length === 0 ? <EmptyState icon={ClipboardCheck} title="No referrals found" /> : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full min-w-[1180px] text-sm" data-testid="referrals-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">Patient</th><th className="px-4 py-3 text-left">Referred by</th><th className="px-4 py-3 text-left">Clinic</th><th className="px-4 py-3 text-left">Assigned rep</th><th className="px-4 py-3 text-left">Patient status</th><th className="px-4 py-3 text-left">Referral status</th><th className="px-4 py-3 text-left">Test Result</th><th className="px-4 py-3 text-left">CPAP Purchased</th><th className="px-4 py-3 text-left">Notes</th><th className="px-4 py-3 text-left">Compliance</th><th className="px-4 py-3 text-right">Actions</th></tr>
              </thead>
              <tbody>{visibleRows.map((row) => (
                <tr key={row.id} className={`border-t border-slate-100 ${pendingStatuses[row.id] || pendingTestResults[row.id] ? "bg-amber-50/60" : ""}`}>
                  <td className="px-4 py-3 font-medium">{canAccessCompliance ? <button type="button" className="text-left text-sky-700 hover:underline" onClick={() => openCompliance(row)}>{row.patient_name}</button> : row.patient_name}</td>
                  <td className="px-4 py-3"><Link className="text-sky-700 hover:underline" to={`/contacts/${row.referrer_contact_id}`}>{row.referrer_name}</Link></td>
                  <td className="px-4 py-3 text-slate-600">{row.organization_name || "Historical clinic unavailable"}</td>
                  <td className="px-4 py-3 text-slate-600">{row.assigned_user_name || "Unassigned"}</td>
                  <td className="px-4 py-3 text-slate-600">{row.patient_lifecycle_status || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="space-y-2">
                      <Select
                        value={pendingStatuses[row.id] || row.status}
                        disabled={!canEdit || !!row.archived || savingRowId === row.id}
                        onValueChange={(value) => stageReferralStatus(row, value as ReferralStatus)}
                      >
                        <SelectTrigger className="w-40" data-testid={`referral-status-${row.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((value) => <SelectItem key={value} value={value}>{value.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                      </Select>
                      {(pendingStatuses[row.id] || pendingTestResults[row.id]) && <div className="space-y-1" data-testid={`referral-unsaved-${row.id}`}>
                        <div className="text-xs font-medium text-amber-700">Unsaved changes</div>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => saveRowChanges(row)} disabled={savingRowId === row.id}>
                            {savingRowId === row.id ? "Saving…" : "Save Changes"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => cancelRowChanges(row.id)} disabled={savingRowId === row.id}>Cancel</Button>
                        </div>
                      </div>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {canViewOsa ? <div className="space-y-2">
                      <Select
                        value={pendingTestResults[row.id] || resultByReferral.get(row.id)?.severity || "pending"}
                        disabled={!!row.archived || savingRowId === row.id || !(resultByReferral.has(row.id) ? canEditOsa : canAddOsa) || !!resultByReferral.get(row.id)?.cpap_purchased}
                        onValueChange={(value) => { if (value !== "pending") stageTestResult(row, value as TestResult); }}
                      >
                        <SelectTrigger className="w-44" data-testid={`test-result-${row.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="pending" disabled>Not tested / pending</SelectItem>{TEST_RESULTS.map((value) => <SelectItem key={value} value={value}>{resultLabel(value)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div> : <span className="text-xs text-slate-400">Not available</span>}
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const result = resultByReferral.get(row.id);
                      if (!result) return <span className="text-slate-500">Awaiting result</span>;
                      if (result.severity === "negative") return <span className="text-slate-500">Not applicable</span>;
                      if (result.cpap_purchased) return <div><span className="font-medium text-emerald-700">Yes</span><div className="text-xs text-slate-500">{result.cpap_purchase_date || "Date recorded"}</div></div>;
                      return canEditOsa && !row.archived
                        ? <Button size="sm" variant="outline" onClick={() => confirmCpapPurchase(row)}>Confirm purchase</Button>
                        : <span className="text-slate-500">No</span>;
                    })()}
                  </td>
                  <td className="max-w-xs px-4 py-3 text-slate-600"><div className="truncate" title={[row.notes, row.remarks].filter(Boolean).join(" · ")}>{[row.notes, row.remarks].filter(Boolean).join(" · ") || "—"}</div></td>
                  <td className="px-4 py-3">{canAccessCompliance ? <Button variant="outline" size="sm" onClick={() => openCompliance(row)}><FileText className="mr-1 h-4 w-4" /> View Latest Report</Button> : <span className="text-xs text-slate-400">Restricted</span>}</td>
                  <td className="px-4 py-3 text-right">{((row.archived && canRestore) || (!row.archived && canArchive)) ? <button className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900" onClick={() => lifecycleReferral(row)}>{row.archived ? <RotateCcw className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}{row.archived ? "Restore" : "Archive"}</button> : <span className="text-xs text-slate-400">View only</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>{total} referral{total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2"><Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button><span>Page {page} of {pages}</span><Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((value) => value + 1)}>Next</Button></div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="referral-dialog">
          <DialogHeader><DialogTitle>Add referral</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Referring contact</Label><Select value={form.referrer_contact_id} onValueChange={selectReferrer}><SelectTrigger><SelectValue placeholder="Choose referring contact" /></SelectTrigger><SelectContent>{referrerContacts.map((row) => <SelectItem key={row.id} value={row.id}>{row.first_name} {row.last_name || ""}</SelectItem>)}</SelectContent></Select></div>
            <div className="col-span-2"><Label>Clinic / hospital (recommended)</Label><Select value={form.organization_id} onValueChange={(value) => setForm({ ...form, organization_id: value })} disabled={!form.referrer_contact_id}><SelectTrigger data-testid="referral-organization"><SelectValue placeholder={form.referrer_contact_id ? "Choose clinic, or leave blank if independent" : "Select a referring contact first"} /></SelectTrigger><SelectContent>{affiliations.map((rel) => <SelectItem key={rel.id} value={rel.organization_id}>{rel.organization_name || "Unnamed organization"}{rel.is_primary ? " · Primary" : ""}</SelectItem>)}</SelectContent></Select>{form.referrer_contact_id && affiliations.length === 0 && <p className="mt-1 text-xs text-amber-700">No clinic is linked. You may continue for an independent practitioner, but please confirm the referral source.</p>}</div>
            <div className="col-span-2"><Label>Patient record</Label><Select value={patientMode} onValueChange={(value) => setPatientMode(value as "new" | "existing")}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new">Create new patient</SelectItem><SelectItem value="existing">Link existing patient</SelectItem></SelectContent></Select></div>
            {patientMode === "existing" ? (
              <div className="col-span-2"><Label>Existing patient</Label><Select value={form.patient_contact_id} onValueChange={(value) => setForm({ ...form, patient_contact_id: value })}><SelectTrigger><SelectValue placeholder="Choose patient" /></SelectTrigger><SelectContent>{availablePatients.map((row) => <SelectItem key={row.id} value={row.id}>{row.first_name} {row.last_name || ""} · {row.lifecycle_status}</SelectItem>)}</SelectContent></Select></div>
            ) : (<>
              <div><Label>First name</Label><Input value={form.first_name} onChange={(event) => setForm({ ...form, first_name: event.target.value })} /></div>
              <div><Label>Last name</Label><Input value={form.last_name} onChange={(event) => setForm({ ...form, last_name: event.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></div>
              <div className="col-span-2"><Label>Address / territory</Label><Input value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} /></div>
            </>)}
            <div><Label>Referral status</Label><Select value={form.status} onValueChange={(value) => setForm({ ...form, status: value as ReferralStatus })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((value) => <SelectItem key={value} value={value}>{value.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} /></div>
            <div className="col-span-2"><Label>Remarks</Label><Input value={form.remarks} onChange={(event) => setForm({ ...form, remarks: event.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => void createReferral()} disabled={saving || !form.referrer_contact_id || (patientMode === "new" ? !form.first_name.trim() : !form.patient_contact_id)}>{saving ? "Saving…" : "Add referral"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={duplicateCandidates.length > 0} onOpenChange={(next) => { if (!next) setDuplicateCandidates([]); }}>
        <DialogContent className="max-w-2xl" data-testid="patient-duplicate-dialog">
          <DialogHeader><DialogTitle>Possible duplicate patient</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Compare these existing tenant records before creating another patient.</p>
          <div id="patient-duplicate-comparison" className="space-y-2">
            {duplicateCandidates.map((candidate) => <label key={candidate.id} className="flex cursor-pointer gap-3 rounded-lg border border-slate-200 p-3">
              <input type="radio" name="duplicate-patient" checked={duplicateCandidateId === candidate.id} onChange={() => setDuplicateCandidateId(candidate.id)} />
              <div className="min-w-0 flex-1"><div className="font-medium">{candidate.first_name} {candidate.last_name || ""}</div>
                <div className="text-xs text-slate-500">{candidate.email || "No email"} · {candidate.phone || "No phone"} · Match {candidate.score}%</div>
                <div className="text-xs text-amber-700">{candidate.reasons.join(", ")}</div>
              </div>
            </label>)}
          </div>
          <div><Label>Reason for saving as a separate patient</Label><textarea className="mt-1 min-h-20 w-full rounded-md border border-slate-200 p-2 text-sm"
            value={duplicateOverrideReason} onChange={(e) => setDuplicateOverrideReason(e.target.value)} placeholder="Required only when this is genuinely a different patient" /></div>
          <DialogFooter className="flex-wrap">
            <Button variant="outline" disabled={!duplicateCandidateId} onClick={() => window.location.assign(`/contacts/${duplicateCandidateId}`)}>Open existing record</Button>
            <Button variant="outline" disabled={!duplicateCandidateId} onClick={() => linkDuplicatePatient(duplicateCandidateId)}>Link to existing record</Button>
            <Button variant="outline" onClick={() => document.getElementById("patient-duplicate-comparison")?.scrollIntoView({ behavior: "smooth" })}>Compare records</Button>
            <Button disabled={!duplicateCandidateId || !duplicateOverrideReason.trim() || saving}
              onClick={() => void createReferral({ candidateId: duplicateCandidateId, reason: duplicateOverrideReason.trim() })}>Save as separate anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={complianceOpen} onOpenChange={setComplianceOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="compliance-dialog">
          <DialogHeader><DialogTitle>Patient Portal · {selectedReferral?.patient_name}</DialogTitle></DialogHeader>

          {!compliance && !editingCompliance && <EmptyState icon={FileText} title="No compliance report uploaded yet" />}

          {compliance && !editingCompliance && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
                <div className="font-semibold">Latest report · {compliance.report_date}</div>
                <div className="mt-1 text-slate-600">Provided by clinician: <strong>{compliance.clinician_name || "Not recorded"}</strong></div><div className="mt-1 text-slate-600">Encoded/uploaded by {compliance.uploaded_by_name || "Unknown user"} · {new Date(compliance.created_at).toLocaleString()}</div>
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div><span className="text-slate-500">AHI</span><div className="font-medium">{compliance.ahi ?? "—"}</div></div>
                  <div><span className="text-slate-500">Leak</span><div className="font-medium">{compliance.leak ?? "—"} {compliance.leak_unit || ""}</div></div>
                  <div><span className="text-slate-500">Pressure</span><div className="font-medium">{compliance.pressure ?? "—"} {compliance.pressure_unit || ""}</div></div>
                  <div><span className="text-slate-500">Usage</span><div className="font-medium">{compliance.usage_percent ?? "—"}%</div></div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => downloadCompliance("source")}><Download className="mr-1 h-4 w-4" /> Source PDF</Button>
                  <Button variant="outline" size="sm" onClick={() => downloadCompliance("doctor-report")}><Download className="mr-1 h-4 w-4" /> Doctor-ready PDF</Button>
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <div className="border-b border-slate-200 px-4 py-3 font-semibold">Report & AHI History</div>
                <table className="w-full min-w-[700px] text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3 text-left">Report date</th><th className="px-4 py-3 text-left">Uploaded file</th><th className="px-4 py-3 text-left">AHI</th><th className="px-4 py-3 text-left">Created by</th><th className="px-4 py-3 text-left">Created</th></tr></thead>
                  <tbody>{reportHistory.map((item) => <tr key={item.id} className="border-t border-slate-100"><td className="px-4 py-3">{item.report_date}</td><td className="px-4 py-3">{item.source_filename}</td><td className="px-4 py-3">{item.ahi ?? "—"}</td><td className="px-4 py-3">{item.uploaded_by_name || "Unknown user"}</td><td className="px-4 py-3">{new Date(item.created_at).toLocaleString()}</td></tr>)}</tbody>
                </table>
              </div>
            </div>
          )}

          {!editingCompliance && (
            <div className="rounded-lg border border-slate-200" data-testid="patient-appointment-history">
              <div className="border-b border-slate-200 px-4 py-3">
                <div className="font-semibold">Appointment History</div>
                <div className="text-xs text-slate-500">Previous Patient Calendar schedules, statuses, and timestamped notes</div>
              </div>
              {appointmentHistory.length === 0 ? (
                <div className="px-4 py-6 text-sm text-slate-500">No Patient Calendar schedules recorded yet.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {appointmentHistory.map((event) => (
                    <div key={event.id} className="space-y-3 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="font-medium">{event.purpose}</div>
                          <div className="text-sm text-slate-600">
                            {new Date(event.start_at).toLocaleString()}
                            {event.end_at ? ` – ${new Date(event.end_at).toLocaleString()}` : ""}
                          </div>
                        </div>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium capitalize">
                          {event.status.replace(/_/g, " ")}
                        </span>
                      </div>
                      <div className="grid gap-2 text-xs text-slate-500 sm:grid-cols-2">
                        <div>Assigned to: <span className="text-slate-700">{event.assigned_to_name}</span></div>
                        <div>Last updated: <span className="text-slate-700">{new Date(event.updated_at || event.created_at).toLocaleString()}</span></div>
                      </div>
                      <div className="space-y-2">
                        {(event.notes || []).length === 0 ? (
                          <div className="text-xs text-slate-400">No notes recorded.</div>
                        ) : (event.notes || []).map((item) => (
                          <div key={item.id} className="rounded-md bg-slate-50 p-3 text-sm">
                            <div>{item.note}</div>
                            <div className="mt-1 text-xs text-slate-500">
                              {item.created_by_name} · {new Date(item.created_at).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {canEditCompliance && editingCompliance && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Report date *</Label><Input type="date" value={complianceForm.report_date} onChange={(e) => setComplianceForm({...complianceForm, report_date:e.target.value})} /></div>
              <div><Label>Clinician who provided the report *</Label><Input value={complianceForm.clinician_name} onChange={(e) => setComplianceForm({...complianceForm, clinician_name:e.target.value})} placeholder="Clinician name" /></div><div><Label>Latest source PDF * (max 5 MB)</Label><Input type="file" accept="application/pdf,.pdf" onChange={(e) => setComplianceFile(e.target.files?.[0] || null)} /></div>
              <div><Label>Period start</Label><Input type="date" value={complianceForm.period_start} onChange={(e) => setComplianceForm({...complianceForm, period_start:e.target.value})} /></div>
              <div><Label>Period end</Label><Input type="date" value={complianceForm.period_end} onChange={(e) => setComplianceForm({...complianceForm, period_end:e.target.value})} /></div>
              <div><Label>AHI</Label><Input type="number" min="0" step="0.1" value={complianceForm.ahi} onChange={(e) => setComplianceForm({...complianceForm, ahi:e.target.value})} /></div>
              <div><Label>Usage hours</Label><Input type="number" min="0" step="0.1" value={complianceForm.usage_hours} onChange={(e) => setComplianceForm({...complianceForm, usage_hours:e.target.value})} /></div>
              <div><Label>Leak</Label><div className="flex gap-2"><Input type="number" min="0" step="0.1" value={complianceForm.leak} onChange={(e) => setComplianceForm({...complianceForm, leak:e.target.value})} /><Input value={complianceForm.leak_unit} onChange={(e) => setComplianceForm({...complianceForm, leak_unit:e.target.value})} /></div></div>
              <div><Label>Pressure</Label><div className="flex gap-2"><Input type="number" min="0" step="0.1" value={complianceForm.pressure} onChange={(e) => setComplianceForm({...complianceForm, pressure:e.target.value})} /><Input value={complianceForm.pressure_unit} onChange={(e) => setComplianceForm({...complianceForm, pressure_unit:e.target.value})} /></div></div>
              <div><Label>Pressure mode</Label><Input value={complianceForm.pressure_mode} onChange={(e) => setComplianceForm({...complianceForm, pressure_mode:e.target.value})} placeholder="CPAP, APAP, BiPAP…" /></div>
              <div><Label>Usage days (used / total)</Label><div className="flex gap-2"><Input type="number" min="0" value={complianceForm.usage_days_used} onChange={(e) => setComplianceForm({...complianceForm, usage_days_used:e.target.value})} /><Input type="number" min="0" value={complianceForm.usage_days_total} onChange={(e) => setComplianceForm({...complianceForm, usage_days_total:e.target.value})} /></div></div>
              <div><Label>Usage percent</Label><Input type="number" min="0" max="100" step="0.1" value={complianceForm.usage_percent} onChange={(e) => setComplianceForm({...complianceForm, usage_percent:e.target.value})} /></div>
              <div className="col-span-2"><Label>Admin Support notes</Label><textarea className="mt-1 min-h-24 w-full rounded-md border border-slate-200 p-2 text-sm" maxLength={4000} value={complianceForm.notes} onChange={(e) => setComplianceForm({...complianceForm, notes:e.target.value})} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => editingCompliance ? setEditingCompliance(false) : setComplianceOpen(false)}>{editingCompliance ? "Cancel" : "Close"}</Button>
            {canEditCompliance && !editingCompliance && <Button onClick={() => setEditingCompliance(true)}>{compliance ? "Update Report" : "Add Report"}</Button>}
            {canEditCompliance && editingCompliance && <Button onClick={saveCompliance} disabled={complianceSaving || !complianceFile || !complianceForm.report_date || !complianceForm.clinician_name.trim()}>{complianceSaving ? "Saving…" : "Save Updated Report"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

